import type { SpaceReadModel, SpaceAssociationPair } from '@client/api';

const GROUP_DISCRIMINATORS = new Set(['Building', 'Generic']);

const isPhysical = (d?: string | null) => !!d && !GROUP_DISCRIMINATORS.has(d);

/** Canonical, order-independent key for an unordered pair. */
export const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

export interface MatrixArea {
  id: string;
  name: string;
  discriminator: string;
}

export interface AssociationMatrix {
  areas: MatrixArea[];
  isAssociated: (a: string, b: string) => boolean;
}

export function buildAssociationMatrix(
  spaces: SpaceReadModel[],
  associations: SpaceAssociationPair[],
): AssociationMatrix {
  // Physical areas only (the sections/rows), ordered by tree path for a stable, hierarchy-aware order.
  const areas: MatrixArea[] = [...spaces]
    .sort((x, y) => (x.path ?? '').localeCompare(y.path ?? ''))
    .filter((s) => isPhysical(s.discriminator))
    .map((s) => ({ id: s.id, name: s.name ?? '', discriminator: s.discriminator }));

  const associatedKeys = new Set(associations.map((p) => pairKey(p.spaceAId, p.spaceBId)));

  return {
    areas,
    isAssociated: (a, b) => a !== b && associatedKeys.has(pairKey(a, b)),
  };
}
